import * as React from 'react'
import { useState } from 'react'
import toast, { Toaster, ToastBar } from 'react-hot-toast'
import { abstractSchema, validateStep } from '../../validation/abstractSchema'
import Header from './submission/Header'
import Overview from './submission/Overview'
import Step1 from './submission/Step1'
import Step2 from './submission/Step2'
import Step3 from './submission/Step3'
import Step4 from './submission/Step4'

const SubmissionForm = ({
  token,
  url,
  logo,
  iconSideToSide,
  iconUpAndDown,
  typeMappings,
  currentUser,
  abstractTncLink,
  submissionDeadline,
}) => {
  const getInitialFormData = () => ({
    // step 1
    category: '',
    mode: '',
    topic: '',
    posterAward: {
      paParticipation: false,
      paAge: null,
      paProofOfIdentity: null,
    },

    // step 2
    institutions: currentUser?.institution ? [currentUser.institution] : [],
    authors: [
      {
        first_name: currentUser?.firstName || '',
        last_name: currentUser?.lastName || '',
        abstract_author_institutions_attributes: currentUser?.institution
          ? [{ abstract_institution_id: currentUser.institution }]
          : [],
        corresponding_email: '',
        is_corresponding: false,
        is_presenting: true,
        position: '',
      },
    ],
    // step 3
    title: '',
    content: '',
    abstractImages: [],
    imagePosition: 'side_to_side',
    keywords: [],
    references: [],
    // references: '',

    // step 4 is a preview step
  })

  const [currentStep, setCurrentStep] = useState(0)
  const [formData, setFormData] = useState(() => getInitialFormData())
  console.log('🚀 ~ SubmissionForm ~ formData:', formData)

  const handleStepChange = (step) => () => {
    setCurrentStep(step)
  }

  // Scroll page to top upon step change gracefully
  React.useEffect(() => {
    toast.dismiss()
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }, [currentStep])

  const handleSubmit = async () => {
    try {
      // Final validation using Zod schema
      const validation = validateStep(formData, abstractSchema)

      if (!validation.isValid) {
        const errorMessages = Object.values(validation.errors)
        throw new Error(`Validation failed:\n\n${errorMessages.join('\n')}`)
      }

      // Prepare form data for submission
      const submitData = new FormData()

      // Add abstract data
      submitData.append('abstract[title]', formData.title.trim())
      submitData.append('abstract[content]', formData.content.trim())
      submitData.append('abstract[image_position]', formData.imagePosition)
      // submitData.append('abstract[references]', formData.references.trim())
      submitData.append('abstract[abstract_category_id]', formData.category)
      submitData.append('abstract[abstract_mode_id]', formData.mode)
      submitData.append('abstract[abstract_topic_id]', formData.topic)

      // Add keywords (filter out empty ones)
      const validKeywords = formData.keywords.filter((k) => k && k.trim())
      validKeywords.forEach((keyword, index) => {
        submitData.append(`abstract[keywords][]`, keyword.trim())
      })

      // Add references (filter out empty ones)
      const validReferences = formData.references.filter(
        (r) => r && r.trim()
      )
      validReferences.forEach((reference, index) => {
        submitData.append(`abstract[references][]`, reference.trim())
      })

      // Add institutions
      formData.institutions.forEach((institution, index) => {
        submitData.append(`abstract[institutions][]`, institution.trim())
      })

      // Add authors
      formData.authors.forEach((author, authorIndex) => {
        submitData.append(
          `abstract[authors][${authorIndex}][first_name]`,
          author.first_name.trim()
        )
        submitData.append(
          `abstract[authors][${authorIndex}][last_name]`,
          author.last_name.trim()
        )
        submitData.append(
          `abstract[authors][${authorIndex}][corresponding_email]`,
          author.corresponding_email ? author.corresponding_email.trim() : ''
        )
        submitData.append(
          `abstract[authors][${authorIndex}][is_corresponding]`,
          author.is_corresponding
        )
        submitData.append(
          `abstract[authors][${authorIndex}][is_presenting]`,
          author.is_presenting
        )
        submitData.append(
          `abstract[authors][${authorIndex}][position]`,
          author.position
        )

        // Add author institutions
        author.abstract_author_institutions_attributes.forEach(
          (inst, instIndex) => {
            submitData.append(
              `abstract[authors][${authorIndex}][abstract_author_institutions_attributes][${instIndex}][abstract_institution_id]`,
              inst.abstract_institution_id.trim()
            )
          }
        )
      })

      // Add images
      if (formData.abstractImages && formData.abstractImages.length > 0) {
        formData.abstractImages.forEach((abstractImage, index) => {
          submitData.append(
            `abstract[abstract_images_attributes][${index}][image]`,
            abstractImage.file
          )
          submitData.append(
            `abstract[abstract_images_attributes][${index}][title]`,
            abstractImage.title
          )
          submitData.append(
            `abstract[abstract_images_attributes][${index}][position]`,
            abstractImage.position
          )
        })
      }

      // Add poster award data
      submitData.append(
        'abstract[poster_award_attributes][pa_participation]',
        formData.posterAward.paParticipation
      )
      if (formData.posterAward.paParticipation) {
        submitData.append(
          'abstract[poster_award_attributes][pa_age]',
          formData.posterAward.paAge
        )
        submitData.append(
          'abstract[poster_award_attributes][pa_proof_of_identity]',
          formData.posterAward.paProofOfIdentity
        )
      }

      // Submit to backend
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'X-CSRF-Token': token,
        },
        body: submitData,
      })

      if (response.ok) {
        const result = await response.json()
        toast(result.message || 'Submission successful!')
        window.location.href = result.redirect_url
      } else {
        const error = await response.json()
        throw new Error(error.error || error.message || 'Submission failed')
      }
    } catch (error) {
      console.error('Submission error:', error)
      throw error
    }
  }

  return (
    <>
      <Toaster
        toastOptions={{
          className: 'cursor-pointer ps-3',
        }}
      >
        {(t) => (
          <ToastBar toast={t}>
            {({ icon, message }) => (
              <div
                onClick={() => toast.dismiss(t.id)}
                className="flex items-center gap-2"
              >
                {icon}
                {message}
              </div>
            )}
          </ToastBar>
        )}
      </Toaster>

      <div
        id="submissionForm"
        className="py-10 bg-white min-h-minus-nav md:py-20"
      >
        <div className="container space-y-8">
          <Header currentStep={currentStep} />

          {currentStep === 0 && (
            <Overview
              abstractTncLink={abstractTncLink}
              logo={logo}
              onNext={handleStepChange(1)}
              submissionDeadline={submissionDeadline}
            />
          )}
          {currentStep === 1 && (
            <Step1
              typeMappings={typeMappings}
              formData={formData}
              setFormData={setFormData}
              setCurrentStep={setCurrentStep}
              currentStep={currentStep}
              onNext={handleStepChange(2)}
              onBack={handleStepChange(0)}
            />
          )}
          {currentStep === 2 && (
            <Step2
              formData={formData}
              setFormData={setFormData}
              currentUser={currentUser}
              onNext={() => setCurrentStep(3)}
              onBack={() => setCurrentStep(1)}
            />
          )}
          {currentStep === 3 && (
            <Step3
              iconSideToSide={iconSideToSide}
              iconUpAndDown={iconUpAndDown}
              formData={formData}
              setFormData={setFormData}
              onNext={() => setCurrentStep(4)}
              onBack={() => setCurrentStep(2)}
            />
          )}
          {currentStep === 4 && (
            <Step4
              formData={formData}
              onBack={() => setCurrentStep(3)}
              onSubmit={handleSubmit}
            />
          )}
        </div>
      </div>
    </>
  )
}

export default SubmissionForm
